import { useCallback, useEffect, useRef, useState } from "react";
import { adminApi } from "../apiAdmin";
import { isNotFound, withQuietRetry } from "../utils/adminRetry";

// A server-side total a tab's KPI cards show (GET /payouts/summary,
// /disputes/summary), added up over EVERY record instead of the loaded page.
//
//   data     the answer to the CURRENT url; null while it loads or failed
//   missing  the server answered 404: it predates the summary route, so the
//            tab adds up its loaded rows exactly as it did before. Sticky for
//            the life of the tab, so an older server is asked once, not on
//            every range switch.
//   failed   the current url's read failed (after the quiet second try);
//            `retry` reads it again
export function useAdminSummary<T>(url: string, parse: (raw: unknown) => T) {
  const [answer, setAnswer] = useState<{ url: string; data: T } | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const missingRef = useRef(false);
  const call = useRef(0);
  const parseRef = useRef(parse);
  parseRef.current = parse;

  const load = useCallback(() => {
    if (missingRef.current) return;
    const mine = ++call.current;
    setFailedUrl(null);
    withQuietRetry(() => adminApi.get(url)).then(
      (r) => {
        if (mine === call.current) setAnswer({ url, data: parseRef.current(r.data) });
      },
      (err) => {
        if (mine !== call.current) return;
        if (isNotFound(err)) {
          missingRef.current = true;
          setMissing(true);
        } else {
          setFailedUrl(url);
        }
      }
    );
  }, [url]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    data: answer && answer.url === url ? answer.data : null,
    missing,
    failed: failedUrl === url,
    retry: load,
  };
}
